import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PDFHeader from './PDFHeader';
import FlightCard from './FlightCard';
import HotelCard from './HotelCard';
import ActivityCard from './ActivityCard';
import SelectedCabCard from './SelectedCabCard';
import PDFRouteTransfers from './PDFRouteTransfers';
import * as FaIcons from 'react-icons/fa';
import * as GiIcons from 'react-icons/gi';
import {
  buildOrderedInclusionItems,
  DEFAULT_INCLUSION_PAGINATION,
  InclusionItem,
  paginateInclusionItems,
} from './inclusionPagination';
import { ItineraryItem, TripInfo } from '../../types/Quotation';
import { getDatePlusDays, parseDateValue } from '../../utils/date';

interface PDFPage2Props {
  highlightImages: string[];
  inclusionImages: string[];
  itinerary: ItineraryItem[];
  addons: Array<{ name: string; price: number; currency: string; description: string }>;
  visaDetails: Array<{ type: string; price: number; validity?: string; documents?: string[]; timeRequired?: string; currency?: string }>;
  insuranceDetails: Array<{ name: string; price: number; currency?: string; description?: string }>;
  inclusions: string[];
  tripInfo?: TripInfo;
  mealPlan?: string;
  selectedRoomCount?: number;
  selectedCab?: {
    cabType?: string;
    maxOccupancy?: number;
    transferType?: string;
  };
  travelStartDate?: string;
  renderedAt?: string;
}

type MeasuredLayout = {
  availableHeight: number;
  titleHeight: number;
  categoryHeadingHeight: number;
  itemHeights: Record<string, number>;
};

const CATEGORY_HEADING_TITLE: Record<InclusionItem['type'], string> = {
  flight: 'Flights',
  hotel: 'Hotels',
  transfer: 'Transfers',
  activity: 'Activities',
};

const PDFPage2: React.FC<PDFPage2Props> = ({
  highlightImages,
  inclusionImages,
  itinerary,
  addons,
  visaDetails,
  insuranceDetails,
  inclusions,
  tripInfo,
  mealPlan,
  selectedRoomCount,
  selectedCab,
  travelStartDate,
  renderedAt,
}) => {
  const FaPlaneDeparture = FaIcons.FaPlaneDeparture as unknown as React.FC<any>;
  const FaHotel = FaIcons.FaHotel as unknown as React.FC<any>;
  const FaShuttleVan = FaIcons.FaShuttleVan as unknown as React.FC<any>;
  const FaMapMarkedAlt = FaIcons.FaMapMarkedAlt as unknown as React.FC<any>;
  const FaPassport = FaIcons.FaPassport as unknown as React.FC<any>;
  const FaShieldAlt = FaIcons.FaShieldAlt as unknown as React.FC<any>;
  const GiMeal = GiIcons.GiMeal as unknown as React.FC<any>;

  const pageRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const categoryHeadingRef = useRef<HTMLDivElement | null>(null);
  const measurementRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [measuredLayout, setMeasuredLayout] = useState<MeasuredLayout | null>(null);

  const IconGlyph: React.FC<{ Icon?: React.FC<any>; alt: string; fallback: string; size?: number }> = ({
    Icon,
    alt,
    fallback,
    size = 20,
  }) => {
    if (!Icon) return <span style={{ fontSize: size }} aria-label={alt}>{fallback}</span>;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-label={alt}>
        <Icon aria-hidden style={{ fontSize: size + 4 }} />
      </span>
    );
  };

  const iconMap: Record<string, React.FC<any> | undefined> = {
    flights: FaPlaneDeparture,
    hotels: FaHotel,
    transfers: FaShuttleVan,
    activities: FaMapMarkedAlt,
    visa: FaPassport,
    insurance: FaShieldAlt,
    meal: GiMeal,
  };

  const visaIncluded = visaDetails.length > 0 || inclusions.includes('visa');
  const insuranceIncluded = insuranceDetails.length > 0 || inclusions.includes('insurance');
  const visaDocuments = Array.from(new Set(visaDetails.flatMap((visa) => visa.documents || [])));
  const resolveHotelStayDates = (hotel: InclusionItem & { hotel?: any }) => {
    const hotelItem = (hotel as any).hotel || hotel;
    const itineraryDayIndex = Math.max(0, Number(hotelItem?.day || 1) - 1);
    const startDate = tripInfo?.startDate || travelStartDate || '';
    const explicitCheckInDate = hotelItem?.checkInDate || '';
    const explicitCheckOutDate = hotelItem?.checkOutDate || '';
    const checkInDate = explicitCheckInDate || (
      travelStartDate
        ? getDatePlusDays(travelStartDate, itineraryDayIndex)
        : ''
    );

    if (!tripInfo?.places?.length || !checkInDate) {
      return {
        checkInDate,
        checkOutDate: explicitCheckOutDate,
      };
    }

    const parsedTripStart = parseDateValue(startDate);
    const parsedCheckIn = parseDateValue(checkInDate);
    const resolvedDayIndex = parsedTripStart && parsedCheckIn
      ? Math.max(
        0,
        Math.round((parsedCheckIn.getTime() - parsedTripStart.getTime()) / (24 * 60 * 60 * 1000))
      )
      : itineraryDayIndex;

    let cursor = 0;
    const matchedPlace = tripInfo.places.find((place) => {
      const nights = Math.max(1, Number(place.nights || 0));
      const start = cursor;
      const end = start + nights - 1;
      cursor += nights;
      return resolvedDayIndex >= start && resolvedDayIndex <= end;
    });
    const stayNights = Math.max(1, Number(matchedPlace?.nights || 0));
    return {
      checkInDate,
      checkOutDate: explicitCheckOutDate || getDatePlusDays(checkInDate, stayNights),
    };
  };
  const inclusionItems = useMemo(() => {
    const baseItems = buildOrderedInclusionItems(itinerary);
    const items = (!selectedCab?.cabType && !selectedCab?.maxOccupancy && !selectedCab?.transferType)
      ? baseItems
      : [...baseItems, {
        key: 'transfer-selected-cab',
        type: 'transfer' as const,
        transfer: {
          cabType: selectedCab?.cabType,
          maxOccupancy: selectedCab?.maxOccupancy,
          transferType: selectedCab?.transferType,
        },
      }];
    const hasRouteDetails = (tripInfo?.routeLegs || []).some((leg) => leg.distanceKm !== undefined || leg.durationMinutes !== undefined || leg.transferNote);
    return hasRouteDetails && !items.some((item) => item.type === 'transfer')
      ? [...items, { key: 'transfer-route-details', type: 'transfer' as const, transfer: {} }]
      : items;
  }, [itinerary, selectedCab?.cabType, selectedCab?.maxOccupancy, selectedCab?.transferType, tripInfo?.routeLegs]);

  useLayoutEffect(() => {
    if (inclusionItems.length === 0) {
      setMeasuredLayout(null);
      return;
    }

    const nextItemHeights = inclusionItems.reduce<Record<string, number>>((acc, item) => {
      const rect = measurementRefs.current[item.key]?.getBoundingClientRect();
      if (rect && rect.height > 0) {
        acc[item.key] = Math.ceil(rect.height);
      }
      return acc;
    }, {});

    const pageRect = pageRef.current?.getBoundingClientRect();
    const sectionRect = sectionRef.current?.getBoundingClientRect();
    const titleRect = titleRef.current?.getBoundingClientRect();
    const categoryRect = categoryHeadingRef.current?.getBoundingClientRect();

    const nextLayout: MeasuredLayout = {
      availableHeight:
        pageRect && sectionRect
          ? (() => {
            const measuredHeight = Math.floor(pageRect.bottom - sectionRect.top - 24);
            return measuredHeight > 0
              ? measuredHeight
              : DEFAULT_INCLUSION_PAGINATION.availableHeight;
          })()
          : DEFAULT_INCLUSION_PAGINATION.availableHeight,
      titleHeight:
        titleRect && titleRect.height > 0
          ? Math.ceil(titleRect.height + 16)
          : DEFAULT_INCLUSION_PAGINATION.titleHeight,
      categoryHeadingHeight:
        categoryRect && categoryRect.height > 0
          ? Math.ceil(categoryRect.height + 16)
          : DEFAULT_INCLUSION_PAGINATION.categoryHeadingHeight,
      itemHeights: nextItemHeights,
    };

    setMeasuredLayout((current) => {
      const currentKey = JSON.stringify(current);
      const nextKey = JSON.stringify(nextLayout);
      return currentKey === nextKey ? current : nextLayout;
    });
  }, [inclusionItems]);

  const inclusionPages = useMemo(
    () =>
      paginateInclusionItems(inclusionItems, {
        ...(measuredLayout || {}),
      }),
    [inclusionItems, measuredLayout]
  );

  const hasSupplementalContent =
    highlightImages.length > 0 ||
    inclusionImages.length > 0 ||
    visaDetails.length > 0 ||
    insuranceDetails.length > 0 ||
    addons.length > 0;

  const seenCategoryHeadings = new Set<InclusionItem['type']>();
  let routeTransferDetailsRendered = false;

  const renderCategoryHeading = (type: InclusionItem['type'], isFirstOnPage: boolean) => (
    <div
      className={`inclusion-section-heading${isFirstOnPage ? ' inclusion-section-heading--first' : ''}`}
      key={`heading-${type}`}
    >
      <IconGlyph
        Icon={iconMap[
          type === 'flight' ? 'flights' : type === 'hotel' ? 'hotels' : type === 'transfer' ? 'transfers' : 'activities'
        ]}
        alt={CATEGORY_HEADING_TITLE[type]}
        fallback={type === 'flight' ? '✈️' : type === 'hotel' ? '🏨' : type === 'transfer' ? '🚐' : '🎟️'}
      />
      <h3>{CATEGORY_HEADING_TITLE[type]}</h3>
    </div>
  );

  const renderInclusionItem = (item: InclusionItem) => {
    if (item.type === 'flight') {
      return <FlightCard flight={item.flight} />;
    }

    if (item.type === 'transfer') {
      if (item.key === 'transfer-route-details') return null;
      return (
        <SelectedCabCard
          cabType={item.transfer.cabType}
          maxOccupancy={item.transfer.maxOccupancy}
          transferType={item.transfer.transferType}
        />
      );
    }

    if (item.type === 'activity') {
      return <ActivityCard activity={item.activity} />;
    }

    const stayDates = resolveHotelStayDates(item);
    return (
      <HotelCard
        hotel={{ ...item.hotel, ...stayDates }}
        variant="inclusion"
        mealPlan={mealPlan}
        selectedRoomCount={selectedRoomCount}
        travelStartDate={travelStartDate}
      />
    );
  };

  const renderSupplementalContent = () => (
    <>
      {(highlightImages.length > 0 || inclusionImages.length > 0) && (
        <div className="pdf-keep-together">
          {highlightImages.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Highlights</div>
              {highlightImages.map((src, idx) => (
                <img key={idx} src={src} alt={`highlight-${idx}`} style={{ maxWidth: '100%', margin: '6px 0', borderRadius: 6 }} />
              ))}
            </div>
          )}
          {inclusionImages.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Gallery</div>
              {inclusionImages.map((src, idx) => (
                <img key={idx} src={src} alt={`inclusion-${idx}`} style={{ maxWidth: '100%', margin: '6px 0', borderRadius: 6 }} />
              ))}
            </div>
          )}
        </div>
      )}

      {visaDetails.length > 0 && (
        <div className="inclusion-card pdf-keep-together">
          <div className="inclusion-card-header">
            <div className="inclusion-card-header-left">
              <span className="inclusion-card-header-icon">
                <IconGlyph Icon={iconMap.visa} alt="Visa" fallback="🛂" size={18} />
              </span>
              <div>
                <div className="inclusion-card-title">Visa</div>
                <div className="inclusion-card-subtitle">{visaIncluded ? 'Visa included' : 'Visa not included'}</div>
              </div>
            </div>
          </div>
          <div className="inclusion-card-body">
            <div className="inclusion-card-media">
              <IconGlyph Icon={iconMap.visa} alt="Visa" fallback="🛂" size={22} />
            </div>
            <div className="inclusion-card-content">
              <div className="inclusion-card-detail-grid">
                {visaDetails.map((visa, idx) => (
                  <div className="inclusion-card-detail" key={`${visa.type}-${idx}`}>
                    <span className="inclusion-card-detail-label">Visa type</span>
                    <strong>{visa.type || 'Visa'}</strong>
                    <div className="inclusion-card-detail-meta">
                      {visa.validity && <span>Valid for {visa.validity}</span>}
                      {visa.timeRequired && <span>Processing: {visa.timeRequired}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {visaDocuments.length > 0 && (
                <div className="inclusion-card-note">
                  <strong>Required documents</strong>
                  <span>{visaDocuments.join(', ')}</span>
                </div>
              )}
              <div className="inclusion-card-muted">Your travel consultant will share the application steps before departure.</div>
            </div>
          </div>
        </div>
      )}

      {insuranceDetails.length > 0 && (
        <div className="inclusion-card pdf-keep-together">
          <div className="inclusion-card-header">
            <div className="inclusion-card-header-left">
              <span className="inclusion-card-header-icon">
                <IconGlyph Icon={iconMap.insurance} alt="Insurance" fallback="🛡️" size={18} />
              </span>
              <div>
                <div className="inclusion-card-title">Insurance</div>
                <div className="inclusion-card-subtitle">
                  {insuranceIncluded ? 'Travel insurance included' : 'Travel insurance not included'}
                </div>
              </div>
            </div>
          </div>
          <div className="inclusion-card-body">
            <div className="inclusion-card-media">
              <IconGlyph Icon={iconMap.insurance} alt="Insurance" fallback="🛡️" size={22} />
            </div>
            <div className="inclusion-card-content">
              {insuranceDetails.map((addon, idx) => {
                const detailItems = (addon.description || '')
                  .split('•')
                  .map((item) => item.trim())
                  .filter(Boolean);
                const items = detailItems.length > 0 ? detailItems : [addon.name || 'Insurance'];
                return (
                  <div className="inclusion-card-detail" key={`${addon.name}-${idx}`}>
                    <span className="inclusion-card-detail-label">Plan</span>
                    <strong>{addon.name || 'Travel insurance'}</strong>
                    <div className="inclusion-card-tags">
                      {items.map((item, itemIdx) => (
                        <span key={`${addon.name}-${idx}-${itemIdx}`}>{item}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="inclusion-card-muted">Policy wording and emergency assistance details will be shared before travel.</div>
            </div>
          </div>
        </div>
      )}

      {addons.length > 0 && (
        <div className="pdf-keep-together" style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 20 }}>➕</span>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: '600', color: '#2c3e50' }}>Add-ons</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {addons.map((addon, idx) => (
              <div key={`${addon.name}-${idx}`} className="pdf-keep-together" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 600 }}>{addon.name}</div>
                {addon.description && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{addon.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderPageItems = (pageItems: InclusionItem[]) => pageItems.map((item, itemIndex) => {
    if (item.type === 'activity') {
      if (pageItems[itemIndex - 1]?.type === 'activity') return null;

      const activities = pageItems.slice(itemIndex).filter((pageItem) => pageItem.type === 'activity');
      const showHeading = !seenCategoryHeadings.has('activity');
      if (showHeading) seenCategoryHeadings.add('activity');

      return (
        <React.Fragment key={`activities-${item.key}`}>
          {showHeading && renderCategoryHeading('activity', itemIndex === 0)}
          <div className="pdf-inclusion-activity-grid">
            {activities.map((activityItem) => (
              <div className="pdf-inclusion-activity-wrapper" key={activityItem.key}>
                {renderInclusionItem(activityItem)}
              </div>
            ))}
          </div>
        </React.Fragment>
      );
    }

    const showHeading = !seenCategoryHeadings.has(item.type);
    if (showHeading) seenCategoryHeadings.add(item.type);
    const showRouteTransferDetails = item.type === 'transfer'
      && !routeTransferDetailsRendered
      && Boolean(tripInfo?.routeLegs?.some((leg) => leg.distanceKm !== undefined || leg.durationMinutes !== undefined || leg.transferNote));
    if (showRouteTransferDetails) routeTransferDetailsRendered = true;

    return (
      <React.Fragment key={item.key}>
        {showHeading && renderCategoryHeading(item.type, itemIndex === 0)}
        {item.key !== 'transfer-route-details' && <div className="pdf-inclusion-card-wrapper">{renderInclusionItem(item)}</div>}
        {showRouteTransferDetails && tripInfo && <PDFRouteTransfers embedded tripInfo={tripInfo} />}
      </React.Fragment>
    );
  });

  return (
    <>
      {inclusionItems.length > 0 && typeof document !== 'undefined' && createPortal(
        <div className="pdf-measurement-root" aria-hidden="true">
          <div className="pdf-page pdf-page--measurement" ref={pageRef}>
            <PDFHeader renderedAt={renderedAt} />
            <section className="pdf-section" ref={sectionRef}>
              <h2 ref={titleRef}>Inclusions</h2>
              <div ref={categoryHeadingRef} className="inclusion-section-heading inclusion-section-heading--first">
                <IconGlyph Icon={iconMap.flights} alt="Flights" fallback="✈️" />
                <h3>Flights</h3>
              </div>
              <div className="pdf-inclusion-card-list">
                {inclusionItems.map((item) => (
                  <div
                    key={`measure-${item.key}`}
                    className="pdf-inclusion-card-wrapper"
                    ref={(node) => {
                      measurementRefs.current[item.key] = node;
                    }}
                  >
                    {renderInclusionItem(item)}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>,
        document.body
      )}

      {inclusionPages.map((pageItems, pageIndex) => (
        <div className="pdf-page" key={`inclusion-page-${pageIndex}`}>
          <PDFHeader renderedAt={renderedAt} />
          <section className="pdf-section">
            <h2>{pageIndex === 0 ? 'Inclusions' : ''}</h2>
            <div className="pdf-inclusion-card-list">
              {renderPageItems(pageItems)}
            </div>
          </section>
        </div>
      ))}

      {hasSupplementalContent && (
        <div className="pdf-page" key="inclusion-supplemental-page">
          <PDFHeader renderedAt={renderedAt} />
          <section className="pdf-section">
            <h2>{inclusionPages.length > 0 ? '' : 'Inclusions'}</h2>
            {renderSupplementalContent()}
          </section>
        </div>
      )}
    </>
  );
};

export default PDFPage2;
